/**
 * Shared utility functions for recipe creation workflows
 * Used by: ChatRecipeCreator, ImageRecipeCreator
 */

import { ai, recipes } from './api';

/**
 * Convert a conversation to a structured recipe JSON
 * Optimized: Single AI call, no fallback loops
 * @param {Array} messages - The conversation messages
 * @param {Object|null} foodPreferences - User's food preferences
 * @returns {Promise<{recipe: Object|null, success: boolean, error: string|null}>}
 */
export const convertConversationToRecipe = async (messages, foodPreferences = null, measurementSystem = null) => {
  try {
    // Find the last assistant message with recipe content
    const recipeMessage = [...messages]
      .reverse()
      .find(m => {
        if (m.role !== 'assistant') return false;
        const lower = m.content.toLowerCase();
        const hasIngredients = lower.includes('ingredients');
        const hasInstructions = lower.includes('instructions') || lower.includes('directions') || lower.includes('step 1');
        return hasIngredients && hasInstructions;
      });
    
    if (!recipeMessage) {
      return { recipe: null, success: false, error: 'No recipe found in conversation' };
    }
    
    // Single extraction prompt - concise and direct
    const extractPrompt = {
      role: 'user',
      content: `Extract this recipe as JSON only. No other text.

${recipeMessage.content}

Return JSON: {title, description, prep_time, cook_time, servings, ingredients: [{name, quantity, unit, preparation, aisle}], instructions: [{step_number, text, duration_minutes, duration_label}], recipe_tips, cuisine_type, difficulty_level, category, tags}

For the "aisle" field in each ingredient, use one of: produce, dairy, meat, bakery, pantry, condiments, spices, frozen, beverages, staples. Use "staples" for common pantry basics most people already have: salt, black pepper, olive oil, flour, sugar, baking soda, baking powder, cornstarch, garlic powder, onion powder, vanilla extract, water. Black pepper is ALWAYS a staple, not a spice. Use "produce" only for fresh fruits and vegetables, NOT for processed products.`
    };
    
    const response = await ai.generateRecipe([extractPrompt], 'generate', foodPreferences, measurementSystem);
    const { recipe, success } = response.data;
    
    if (success && recipe) {
      return { recipe, success: true, error: null };
    }

    return { recipe: null, success: false, error: 'Failed to extract recipe' };
  } catch (error) {
    console.error('Recipe conversion error:', error);
    return { recipe: null, success: false, error: error.message };
  }
};

/**
 * Extract a single specific recipe from a conversation by name
 * @param {Array} messages - The conversation messages
 * @param {string} recipeName - The name of the recipe to extract
 * @param {Object|null} foodPreferences - User's food preferences
 * @returns {Promise<{recipe: Object|null, success: boolean, error: string|null}>}
 */
export const extractSingleRecipe = async (messages, recipeName, foodPreferences = null, measurementSystem = null) => {
  try {
    // Find messages that mention this recipe
    const relevantContent = messages
      .filter(m => m.role === 'assistant' && m.content.toLowerCase().includes(recipeName.toLowerCase()))
      .map(m => m.content)
      .join('\n\n');
    
    if (!relevantContent) {
      return { recipe: null, success: false, error: 'Recipe not found in conversation' };
    }
    
    // Extraction prompt specifically for this recipe
    const extractPrompt = {
      role: 'user',
      content: `Extract ONLY the recipe for "${recipeName}" from this text as JSON. No other text.

${relevantContent}

Return JSON: {title, description, prep_time, cook_time, servings, ingredients: [{name, quantity, unit, preparation, aisle}], instructions: [{step_number, text, duration_minutes, duration_label}], recipe_tips, cuisine_type, difficulty_level, category, tags}

For the "aisle" field in each ingredient, use one of: produce, dairy, meat, bakery, pantry, condiments, spices, frozen, beverages, staples. Use "staples" for common pantry basics most people already have: salt, black pepper, olive oil, flour, sugar, baking soda, baking powder, cornstarch, garlic powder, onion powder, vanilla extract, water. Black pepper is ALWAYS a staple, not a spice. Use "produce" only for fresh fruits and vegetables, NOT for processed products.`
    };
    
    const response = await ai.generateRecipe([extractPrompt], 'generate', foodPreferences, measurementSystem);
    const { recipe, success } = response.data;
    
    if (success && recipe) {
      return { recipe, success: true, error: null };
    }

    return { recipe: null, success: false, error: 'Failed to extract recipe' };
  } catch (error) {
    console.error('Single recipe extraction error:', error);
    return { recipe: null, success: false, error: error.message };
  }
};

/**
 * Save a recipe to the database
 * @param {Object} recipeData - The recipe data to save
 * @param {string} generationMethod - How the recipe was created ('chat', 'image_upload')
 * @returns {Promise<{recipeId: string|null, success: boolean, error: string|null}>}
 */
export const saveRecipe = async (recipeData, generationMethod = 'chat', sourceUrl = null, modifications = [], sourceImage = null) => {
  try {
    console.log('[saveRecipe] Starting save with title:', recipeData.title);
    const payload = {
      ...recipeData,
      generation_method: generationMethod,
    };
    
    // Add source URL if provided (for URL imports)
    if (sourceUrl) {
      payload.source_url = sourceUrl;
      // URL-imported recipes have nutrition scraped from the source website
      // Override any AI-estimated tag from the extraction step
      if (payload.nutrition) {
        payload.nutrition_source = 'scraped';
      }
    }
    
    // Add source image if provided (from URL imports or Paprika)
    if (sourceImage) {
      payload.source_image = sourceImage;
    }
    console.log('[saveRecipe] Payload source_image:', payload.source_image ? 'SET (' + payload.source_image.substring(0, 50) + '...)' : 'NOT SET');
    
    // Add modifications if provided (for pre-save chat modifications)
    if (modifications && modifications.length > 0) {
      payload.modifications = modifications;
    }
    
    const response = await recipes.create(payload);
    console.log('[saveRecipe] API response:', JSON.stringify(response.data).substring(0, 200));
    const recipeId = response.data.recipe_id;
    console.log('[saveRecipe] Extracted recipe_id:', recipeId);
    
    // Verify we got a valid recipe_id
    if (!recipeId || typeof recipeId !== 'string' || !recipeId.startsWith('recipe_')) {
      console.error('[saveRecipe] Invalid recipe_id received:', recipeId);
      return { recipeId: null, success: false, error: 'Invalid recipe ID received from server' };
    }
    
    // IMPORTANT: Invalidate recipe cache so RecipeBox fetches fresh data
    // This ensures newly created recipes appear immediately
    localStorage.removeItem('recipesCache');
    localStorage.removeItem('recipesCacheTime');
    
    // Trigger background AI enhancement for aisles and category (don't wait for it)
    // This silently improves data quality after the instant save
    recipes.enhanceWithAisles(recipeId).catch(err => {
      console.log('Background recipe enhancement failed (non-critical):', err);
    });
    
    console.log('[saveRecipe] Returning success with recipeId:', recipeId);
    return { recipeId, success: true, error: null };
  } catch (error) {
    console.error('Recipe save error:', error);
    return { recipeId: null, success: false, error: error.message };
  }
};

/**
 * Send a chat message and get AI response
 * @param {Array} messages - All conversation messages including the new one
 * @param {Object|null} foodPreferences - User's food preferences
 * @returns {Promise<{message: string|null, recipe: Object|null, success: boolean, error: string|null}>}
 */
export const sendChatMessage = async (messages, foodPreferences = null, measurementSystem = null) => {
  try {
    const response = await ai.generateRecipe(messages, 'conversation', foodPreferences, measurementSystem);
    const { message, recipe } = response.data;
    return { message, recipe, success: true, error: null };
  } catch (error) {
    console.error('Chat error:', error);
    
    let errorMessage = 'Sorry, I encountered an error. Please try again.';
    
    // Check for budget error
    if (error.response?.status === 402 || error.response?.data?.detail?.includes('budget')) {
      errorMessage = "⚠️ The AI budget limit has been reached. To continue:\n\n1. Go to Profile → Universal Key → Add Balance to top up, OR\n2. Use your own OpenAI API key";
    } else if (error.response?.data?.detail) {
      errorMessage = `Error: ${error.response.data.detail}`;
    }
    
    return { message: errorMessage, recipe: null, success: false, error: error.message };
  }
};

/**
 * Analyze images and get AI response
 * @param {Array} images - Array of image objects with base64 property
 * @param {string|null} imageType - 'recipe_card', 'dish_photo', or null for auto-detect
 * @param {Object|null} foodPreferences - User's food preferences
 * @returns {Promise<{message: string|null, success: boolean, error: string|null}>}
 */
export const analyzeImages = async (images, imageType = null, foodPreferences = null, measurementSystem = null) => {
  try {
    const primaryImage = images[0].base64;
    const additionalImages = images.length > 1 ? images.slice(1).map(img => img.base64) : null;
    
    const response = await ai.analyzeImage(primaryImage, imageType, additionalImages, foodPreferences, measurementSystem);
    const { message, success } = response.data;
    
    return { message, success, error: null };
  } catch (error) {
    console.error('Image analysis error:', error);
    return { message: null, success: false, error: error.message };
  }
};
